import { useCallback, useState } from 'react';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import {
  QuestionnaireStatus,
  useDeleteFormManagementQuestionnaireMutation,
  useUpdateFormManagementQuestionnaireStatusMutation,
} from 'src/graphql';
import { FormData } from './types';

export interface UseFormManagementRowMutationsArgs {
  refetchForms: () => void;
  refetchFormsAndCategories: () => void;
}

export function useFormManagementRowMutations({
  refetchForms,
  refetchFormsAndCategories,
}: UseFormManagementRowMutationsArgs) {
  const [formToArchive, setFormToArchive] = useState<FormData | null>(null);
  const [formToUnpublish, setFormToUnpublish] = useState<FormData | null>(null);
  const [formToDelete, setFormToDelete] = useState<FormData | null>(null);

  const statusMutation = useUpdateFormManagementQuestionnaireStatusMutation();
  const deleteMutation = useDeleteFormManagementQuestionnaireMutation();

  const handleArchive = useCallback((form: FormData) => {
    setFormToArchive(form);
  }, []);

  const handleArchiveConfirm = useCallback(() => {
    if (!formToArchive) return;
    statusMutation.mutate(
      { id: formToArchive.id, input: { status: QuestionnaireStatus.Archived } },
      {
        onSuccess: () => {
          handleSuccessToast({
            toastMessage: 'Form archived',
            toastDescription: `"${formToArchive.title}" has been archived.`,
          });
          setFormToArchive(null);
          refetchForms();
        },
        onError: () => {
          handleErrorToast({
            toastMessage: 'Failed to archive form',
            toastDescription: 'Something went wrong. Please try again.',
          });
          setFormToArchive(null);
        },
      },
    );
  }, [formToArchive, statusMutation, refetchForms]);

  const handleUnpublish = useCallback((form: FormData) => {
    setFormToUnpublish(form);
  }, []);

  const handleUnpublishConfirm = useCallback(() => {
    if (!formToUnpublish) return;
    statusMutation.mutate(
      {
        id: formToUnpublish.id,
        input: { status: QuestionnaireStatus.Inactive },
      },
      {
        onSuccess: () => {
          handleSuccessToast({
            toastMessage: 'Form unpublished',
            toastDescription: `"${formToUnpublish.title}" has been unpublished.`,
          });
          setFormToUnpublish(null);
          // Unpublishing the last published form flips its category back to
          // "Form required", so the category data has to be refetched too.
          refetchFormsAndCategories();
        },
        onError: () => {
          handleErrorToast({
            toastMessage: 'Failed to unpublish form',
            toastDescription: 'Something went wrong. Please try again.',
          });
          setFormToUnpublish(null);
        },
      },
    );
  }, [formToUnpublish, statusMutation, refetchFormsAndCategories]);

  const handleDelete = useCallback((form: FormData) => {
    setFormToDelete(form);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!formToDelete) return;
    deleteMutation.mutate(
      { id: formToDelete.id },
      {
        onSuccess: (data) => {
          const response = data.deleteFormManagementQuestionnaire;
          if (response.success) {
            handleSuccessToast({
              toastMessage: 'Draft deleted',
              toastDescription: `"${formToDelete.title}" has been deleted.`,
            });
            setFormToDelete(null);
            refetchForms();
          } else {
            handleErrorToast({
              toastMessage: 'Failed to delete draft',
              toastDescription:
                response.message ||
                "We couldn't delete your draft. Please try again.",
            });
            setFormToDelete(null);
          }
        },
        onError: () => {
          handleErrorToast({
            toastMessage: 'Failed to delete draft',
            toastDescription:
              "We couldn't delete your draft. Please try again or refresh the page.",
          });
          setFormToDelete(null);
        },
      },
    );
  }, [formToDelete, deleteMutation, refetchForms]);

  return {
    formToArchive,
    setFormToArchive,
    handleArchive,
    handleArchiveConfirm,
    formToUnpublish,
    setFormToUnpublish,
    handleUnpublish,
    handleUnpublishConfirm,
    formToDelete,
    setFormToDelete,
    handleDelete,
    handleDeleteConfirm,
    deleteMutationLoading: deleteMutation.isLoading,
    unpublishMutationLoading: statusMutation.isLoading,
  };
}
